import { useState, useEffect, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";

// Pages
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import FixturesPage from "./pages/FixturesPage";
import HistoryPage from "./pages/HistoryPage";
import PlayersPage from "./pages/PlayersPage";
import CupsPage from "./pages/CupsPage";
import EntertainmentPage from "./pages/EntertainmentPage";
import ForumPage from "./pages/ForumPage";
import SubscriptionPage from "./pages/SubscriptionPage";
import ProfilePage from "./pages/ProfilePage";
import AdminPage from "./pages/AdminPage";
import TablesPage from "./pages/TablesPage";
import TopStatsPage from "./pages/TopStatsPage";
import ShopPage from "./pages/ShopPage";
import NewsPage from "./pages/NewsPage";
import EmailVerifyPage from "./pages/EmailVerifyPage";
import FanClubsPage from "./pages/FanClubsPage";
import FanMemoriesPage from "./pages/FanMemoriesPage";
import MatchStreakPage from "./pages/MatchStreakPage";
import TVPage from "./pages/TVPage";
import ScorePredictorPage from "./pages/ScorePredictorPage";
import FAQPage from "./pages/FAQPage";
import OnThisDayPage from "./pages/OnThisDayPage";
import ChantsPage from "./pages/ChantsPage";
import MemesPage from "./pages/MemesPage";
import TransfersPage from "./pages/TransfersPage";
import TransferNewsDetailPage from "./pages/TransferNewsDetailPage";
import AuthCallback from "./pages/AuthCallback";
import NewsArticlePage from "./pages/NewsArticlePage";
import PlayerProfilePage from "./pages/PlayerProfilePage";
import PlayerComparePage from "./pages/PlayerComparePage";
import FanClubDetailPage from "./pages/FanClubDetailPage";
import FanMemoryDetailPage from "./pages/FanMemoryDetailPage";
import ChantDetailPage from "./pages/ChantDetailPage";

// Always use relative URLs for API calls - works in all environments
// The Kubernetes ingress routes /api to the backend automatically
export const API = "/api";

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
        } catch (error) {
          localStorage.removeItem("token");
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };
    checkAuth();
  }, [token]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    localStorage.setItem("token", response.data.access_token);
    setToken(response.data.access_token);
    setUser(response.data.user);
    return response.data;
  };

  const register = async (data) => {
    const response = await axios.post(`${API}/auth/register`, data);
    localStorage.setItem("token", response.data.access_token);
    setToken(response.data.access_token);
    setUser(response.data.user);
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
    toast.success("Logged out successfully");
  };

  const refreshUser = async () => {
    if (token) {
      try {
        const response = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUser(response.data);
      } catch (error) {
        console.error("Failed to refresh user");
      }
    }
  };

  /**
   * Set the auth state from a token acquired outside the normal
   * login/register flow (e.g. after Emergent Google OAuth callback).
   */
  const setAuthFromToken = (accessToken, userData) => {
    localStorage.setItem("token", accessToken);
    setToken(accessToken);
    setUser(userData);
  };

  return (
    <AuthContext.Provider value={{ user, token, login, register, logout, loading, refreshUser, setAuthFromToken }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children, requireSubscription = false, requireAdmin = false }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (requireAdmin && !user.is_admin) {
    return <Navigate to="/" replace />;
  }

  if (requireSubscription && !user.is_subscribed) {
    return <Navigate to="/subscription" replace />;
  }

  return children;
};

// Page Tracking Hook
const usePageTracking = () => {
  const location = useLocation();
  const { token } = useAuth();
  
  useEffect(() => {
    // Track page visit
    const trackPage = async () => {
      try {
        await axios.post(`${API}/analytics/track-page?page=${encodeURIComponent(location.pathname)}`, null, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
      } catch (error) {
        // Silent fail - don't disrupt user experience
      }
    };
    
    trackPage();
  }, [location.pathname, token]);
};

function AppContent() {
  usePageTracking();
  
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/auth-callback" element={<AuthCallback />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/verify-email/:token" element={<EmailVerifyPage />} />
      <Route path="/fixtures" element={<FixturesPage />} />
      <Route path="/tables" element={<TablesPage />} />
      <Route path="/stats" element={<TopStatsPage />} />
      <Route path="/history" element={<HistoryPage />} />
      <Route path="/players" element={<PlayersPage />} />
      <Route path="/players/compare" element={<PlayerComparePage />} />
      <Route path="/players/:slug" element={<PlayerProfilePage />} />
      <Route path="/cups" element={<CupsPage />} />
      <Route path="/entertainment" element={<EntertainmentPage />} />
      <Route path="/shop" element={<ShopPage />} />
      <Route path="/news" element={<NewsPage />} />
      <Route path="/news/:slug" element={<NewsArticlePage />} />
      <Route path="/fan-clubs" element={<FanClubsPage />} />
      <Route path="/fan-clubs/:slug" element={<FanClubDetailPage />} />
      <Route path="/fan-memories" element={<FanMemoriesPage />} />
      <Route path="/fan-memories/:slug" element={<FanMemoryDetailPage />} />
      <Route path="/match-streak" element={<MatchStreakPage />} />
      <Route path="/forum" element={<ForumPage />} />
      <Route path="/mahjong" element={<Navigate to="/" replace />} />
      <Route path="/tv" element={<TVPage />} />
      <Route path="/score-predictor" element={<ScorePredictorPage />} />
      <Route path="/faq" element={<FAQPage />} />
      <Route path="/on-this-day" element={<OnThisDayPage />} />
      <Route path="/chants" element={<ChantsPage />} />
      <Route path="/chants/:slug" element={<ChantDetailPage />} />
      <Route path="/west-ham-memes" element={<MemesPage />} />
      <Route path="/transfers" element={<TransfersPage />} />
      <Route path="/transfers/news/:slug" element={<TransferNewsDetailPage />} />
      <Route path="/subscription" element={<SubscriptionPage />} />
      <Route path="/subscription/success" element={<SubscriptionPage />} />
      <Route 
        path="/profile" 
        element={
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/*" 
        element={
          <ProtectedRoute requireAdmin>
            <AdminPage />
          </ProtectedRoute>
        } 
      />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-right" richColors />
        <AppContent />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
